/**
 * @prettier
 */

/**
 * Attempt to split up these routes and make this more manageable
 */
import React from 'react';
import { Route, IndexRoute, IndexRedirect } from 'react-router';
import { trackOnboardingCompleted, onboardTrackingConstants } from '@zola-helpers/client';

import scrollViewTo from '../util/scrollViewTo';
import routeSniffer from '../util/routeSniffer';
import { isDesktop } from '../lib/responsive';
import FeatureFlags from '../util/featureFlags';

// Actions
import { getGuestRequestCount } from '../actions/PublicWebsiteActions';
import { fetchGuestList } from '../actions/GuestActions';
import { fetchEventsRsvpPage, fetchEventsByDate } from '../actions/EventActions';
import { fetchChart, fetchChartList, finishOnboarding } from '../actions/SeatingChartActions';
import { submitOnboarding } from '../actions/OnboardActions';
import { fetchWeddingAccount } from '../actions/WeddingActions';
import { getUserContext } from '../actions/UserActions';

// Selectors
import { getUserHasCompletedOnboarding, getUserHasWeddingAccount } from '../selectors/user/userSelectors';

// Components
import ThemesContainer from '../components/manage/website/ThemesContainer';
import ThemeDetail from '../components/manage/website/ThemeDetail';
import EditPage from '../components/manage/website/EditPage';

import Directory from '../components/manage/guestlist/Directory';
import Build from '../components/manage/guestlist/Build';
import AddressEnvelopes from '../components/manage/guestlist/AddressEnvelopes';
import RsvpOverview from '../components/manage/guestlist/RsvpOverview';
import TrackRsvpNotifications from '../components/manage/guestlist/trackRsvpNotifications/TrackRsvpNotifications';
import MultipleRsvpNotifications from '../components/manage/guestlist/trackRsvpNotifications/MultipleRsvpNotifications'; // eslint-disable-line
import AddGuestPage from '../components/manage/guestlist/addGuestPage/AddGuestPage';
import RsvpEvent from '../components/manage/guestlist/RsvpEvent';
import SeatingChart from '../components/manage/seatingchart/SeatingChart';
import ChecklistContainer from '../components/manage/ChecklistV2';

const { BUSINESS_UNIT, COMPONENT } = onboardTrackingConstants;
const getWeddingManageRoutes = store => {
  const redirect = ({ to, useSlug }) => nextState => {
    const slug = useSlug ? `/${nextState.params[useSlug]}` : '';
    window.location = `${to}${slug}`;
  };

  const checkRegistryAndRedirect = () => {
    const { dispatch } = store;

    dispatch(getUserContext())
      .then(() => {
        const state = store.getState();
        if (state.user.userContext.registry_ids.length > 0) {
          window.location = '/registry/dashboard';
        } else {
          window.location = '/onboard/new';
        }
      })
      .catch(() => {
        window.location = '/onboard/new';
      });
  };

  const checkWeddingAccountForPlanningTools = (nextState, replaceState, callback) => {
    const { dispatch, getState } = store;
    const state = getState();
    const { pathname } = nextState.location;
    const planningDashboard = pathname === '/wedding/manage';
    const weddingManageRegistry =
      pathname === '/wedding/manage/registry-onboard' || pathname === '/wedding/manage/registry';

    if (!getUserHasCompletedOnboarding(state)) {
      if (planningDashboard) {
        // Send to wedding planning onboarding flow
        replaceState('/wedding/onboard?product=WEDDING_PLANNING');
        callback();
      } else if (weddingManageRegistry) {
        // Send to registry onboarding flow
        window.location.href = '/onboard/new';
      } else {
        // Send to planning tools landing page (all others requires a weddings account)
        window.location.href = '/wedding-planning';
      }
    }

    // Auto-onboard existing users to Planning Dashboard
    if (planningDashboard && !getUserHasWeddingAccount(state)) {
      // create a wedding account
      const onboardingData = {
        existing_account: true,
        business_unit: COMPONENT.WEDDING_PLANNING,
        answers: [],
      };
      dispatch(submitOnboarding(onboardingData)).then(() => {
        trackOnboardingCompleted(BUSINESS_UNIT.WEDDINGS, COMPONENT.WEDDING_PLANNING, { isExistingUser: true });
        trackOnboardingCompleted(BUSINESS_UNIT.WEDDINGS, COMPONENT.WEDDINGS, { isExistingUser: true });
        replaceState('/wedding/manage');
        callback();
      });
    } else if (!getUserHasWeddingAccount(state)) {
      // Send to planning tools landing page (all others requires a weddings account)
      window.location.href = '/wedding-planning';
    } else {
      dispatch(fetchWeddingAccount())
        .then(() => {
          callback();
        })
        .catch(() => {
          // Send to pre-auth planning tools landing page if failed to fetch wedding account
          window.location.href = '/wedding-planning';
        });
    }
  };

  const isOnboarding = (nextState, replaceState, callback) => {
    const { dispatch } = store;
    dispatch(fetchWeddingAccount())
      .then(() => {
        const state = store.getState();
        if (!state.wedding.account.disable_website_onboarding) {
          replaceState('/wedding/manage/website/designs');
        }
        callback();
      })
      .catch(() => {
        window.location.href = '/wedding-planning';
      });
  };

  const checkEvent = (nextState, replaceState, callback) => {
    const { dispatch } = store;
    const { eventUUID } = nextState.params;
    dispatch(fetchEventsRsvpPage())
      .then(() => {
        const { events } = store.getState().events;
        const eventId = events.uuidToId[eventUUID];
        if (!eventId) {
          replaceState('/wedding/manage/guests/rsvps/overview');
        }
        callback();
      })
      .catch(() => {
        replaceState('/wedding/manage/guests/rsvps/overview');
        callback();
      });
  };

  const checkGuestRequests = (nextState, replaceState, callback) => {
    const { dispatch } = store;
    dispatch(getGuestRequestCount())
      .then(() => getGuestRequestCount())
      .then(() => {
        const notificationCount = store.getState().publicWebsite.guestRequestCount;
        if (notificationCount === 0) {
          replaceState('/wedding/manage/guests/all');
        }
        callback();
      })
      .catch(() => {
        replaceState('/wedding/manage/guests/all');
        callback();
      });
  };

  const checkGuestList = (nextState, replaceState, callback) => {
    const { dispatch } = store;
    dispatch(fetchGuestList()).then(() => {
      callback();
    });
  };

  // onEnter hook for Seating Chart landing
  const fetchAllSeatingCharts = (nextState, replaceState, callback) => {
    const { dispatch } = store;
    dispatch(fetchEventsByDate());
    dispatch(fetchChartList()).then(() => callback());
  };

  // Fetch seating chart by uuid
  const fetchSeatingChart = (nextState, replaceState, callback) => {
    const { dispatch } = store;
    const { uuid } = nextState.params;
    if (uuid != null) {
      dispatch(fetchChart(uuid))
        .catch(() => {
          replaceState('/notfound');
          callback();
        })
        .then(() => {
          dispatch(fetchEventsByDate());
          dispatch(finishOnboarding());
          dispatch(fetchChartList()).then(() => callback());
        });
    } else {
      // To handle browser back/forward buttons during/directly after onboarding (will need to change with more entry points)
      const state = store.getState();
      if (state.seatingChart != null && state.seatingChart.onboarding) {
        callback();
      } else {
        if (FeatureFlags.get('seatingChartLanding')) {
          replaceState('/wedding/manage/seating');
        } else {
          replaceState('/wedding/manage/events');
        }
        callback();
      }
    }
  };

  const renderUserChecklist = () => {
    const userIsSelectingDifferentTask = (prevState, nextState) => {
      const prevUrl = routeSniffer(prevState.location.pathname, 'checklistTask', false) || {};
      const nextUrl = routeSniffer(nextState.location.pathname, 'checklistTask', false) || {};

      if (prevUrl.isExact && nextUrl.isExact && isDesktop())
        scrollViewTo({ id: nextUrl.scrollLocationId, offset: nextUrl.offset, behavior: 'smooth' });
    };

    return (
      <React.Fragment>
        <Route path="wedding-checklist" onChange={userIsSelectingDifferentTask}>
          <IndexRoute
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(null, require('../components/manage/ChecklistV2').default);
                },
                'ChecklistV2'
              );
            }}
          />
          <Route path=":id" component={ChecklistContainer} />
        </Route>
        <Route path="checklist" onEnter={redirect({ to: '/wedding/manage/wedding-checklist' })} />
      </React.Fragment>
    );
  };

  return (
    <Route
      path="manage"
      getComponent={(location, callback) => {
        require.ensure(
          [],
          require => {
            callback(null, require('../pages/Manage').default);
          },
          'Manage'
        );
      }}
      onEnter={checkWeddingAccountForPlanningTools}
    >
      <IndexRoute
        getComponent={(location, callback) => {
          require.ensure(
            [],
            require => {
              callback(null, require('../pages/dashboardContainer').default);
            },
            'DashboardContainer'
          );
        }}
      />
      {/* TODO: I think this route needs to be redirected to Events.  Confirm. */}
      {/* <Route
      path="feed"
      getComponent={(location, callback) => {
        require.ensure([], (require) => {
          callback(null, require('../components/manage/feed/Feed').default);
        }, 'Feed');
      }}
    /> */}
      <Route
        path="events"
        getComponent={(location, callback) => {
          require.ensure(
            [],
            require => {
              callback(null, require('../pages/EventsContainer').default);
            },
            'Events'
          );
        }}
      />
      {renderUserChecklist()}
      <Route path="registry" onEnter={checkRegistryAndRedirect} />
      <Route path="registry-onboard" onEnter={checkRegistryAndRedirect} />
      <Route
        path="guests"
        getComponent={(location, callback) => {
          require.ensure(
            [],
            require => {
              callback(null, require('../pages/GuestsV2').default);
            },
            'GuestsV2'
          );
        }}
        onEnter={checkGuestList} // TODO: specific children are calling different lists, move onEnters to children
      >
        <IndexRedirect to="all" />
        <Route path="all" component={Directory} />
        <Route path="build" component={Build} />
        <Route path="envelopes" component={AddressEnvelopes} />
        <Route>
          <Route path="rsvp-notification/:rsvpUUID" component={TrackRsvpNotifications} onEnter={checkGuestRequests} />
          <Route path="rsvp-notifications" component={MultipleRsvpNotifications} />
          <Route path="add-guest/:rsvpUUID" component={AddGuestPage} />
        </Route>
        <Route
          path="rsvps"
          getComponent={(location, callback) => {
            require.ensure(
              [],
              require => {
                callback(null, require('../components/manage/guestlist/TrackRsvpsContainer').default);
              },
              'TrackRsvpsContainer'
            );
          }}
        >
          <IndexRedirect to="overview" />
          <Route path="overview" component={RsvpOverview} />
          <Route path=":eventUUID" component={RsvpEvent} onEnter={checkEvent} />
        </Route>
        <Route path="thank-you-notes" />
      </Route>
      <Route>
        {FeatureFlags.get('seatingChartLanding') && (
          <Route
            path="seating"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(null, require('../components/manage/seatingchart/SeatingList').default);
                },
                'SeatingList'
              );
            }}
            onEnter={fetchAllSeatingCharts}
          />
        )}
        <Route
          path="seating-chart"
          getComponent={(location, callback) => {
            require.ensure(
              [],
              require => {
                callback(null, require('../components/manage/seatingchart/Onboarding/Onboarding').default);
              },
              'Onboarding'
            );
          }}
          onEnter={fetchSeatingChart}
        />
        <Route path="seating-chart/:uuid" component={SeatingChart} onEnter={fetchSeatingChart} />
      </Route>
      <Route
        path="website"
        getComponent={(location, callback) => {
          require.ensure(
            [],
            require => {
              callback(null, require('../pages/Website').default);
            },
            'Website'
          );
        }}
      >
        <IndexRedirect to="manage" />
        <Route
          path="pages"
          onEnter={isOnboarding}
          getComponent={(location, callback) => {
            if (FeatureFlags.get('isWebsiteEditPagesV2')) {
              require.ensure(
                [],
                require => {
                  callback(null, require('../components/manage/website/WebsiteEditPages/WebsiteEditPages').default);
                },
                'Pages'
              );
            } else {
              require.ensure(
                [],
                require => {
                  callback(null, require('../components/manage/website/Pages').default);
                },
                'Pages'
              );
            }
          }}
        >
          <IndexRedirect to="home" />
          <Route path=":pageKey" component={EditPage} />
        </Route>
        <Route
          path="manage"
          onEnter={isOnboarding}
          getComponent={(location, callback) => {
            require.ensure(
              [],
              require => {
                callback(null, require('../components/manage/website/ManagePagesRedesign/ManagePagesRedesign').default);
              },
              'ManagePagesRedesign'
            );
          }}
        />
        <Route
          path="themes"
          onEnter={redirect({ to: '/wedding/manage/website/designs' })}
          component={ThemesContainer}
        />
        <Route
          path="theme/:themeKey"
          onEnter={redirect({ to: '/wedding/manage/website/design', useSlug: 'themeKey' })}
        />
        <Route path="designs" component={ThemesContainer} />
        <Route path="design/:themeKey" component={ThemeDetail} />
      </Route>
    </Route>
  );
};

export default getWeddingManageRoutes;



// WEBPACK FOOTER //
// ./src/routes/weddingManageRoutes.jsx